/**
 * Loads the signed-in user's effective measurement system (decision S4) from
 * GET /users/me/preferences and hands it to useMeasurementSystem. Mounted once
 * in App; renders nothing. Settings pages invalidate MEASUREMENT_PREFERENCES_KEY
 * after a change so every screen switches units at once.
 */
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { getMyMeasurementPreferences } from '@/api/userPreferences';
import { selectCurrentUser, selectIsAuthenticated } from '@/store/slices/authSlice';
import { restoreMeasurementSystem, setMeasurementSystem } from '@/hooks/useMeasurementSystem';

/** React Query key of the caller's measurement preferences. */
export const MEASUREMENT_PREFERENCES_KEY = 'measurement-preferences';

export default function MeasurementPreferencesSync(): null {
  const user = useSelector(selectCurrentUser);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const userId = user?.id ?? null;
  const orgId = (user?.impersonatedOrgId ?? user?.organizationId) ?? null;

  useEffect(() => {
    restoreMeasurementSystem(isAuthenticated ? userId : null);
  }, [isAuthenticated, userId]);

  const { data } = useQuery({
    queryKey: [MEASUREMENT_PREFERENCES_KEY, userId, orgId],
    queryFn: () => getMyMeasurementPreferences(),
    enabled: Boolean(isAuthenticated && userId && orgId),
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    if (data?.effectiveMeasurementSystem) setMeasurementSystem(data.effectiveMeasurementSystem, userId);
  }, [data, userId]);

  return null;
}
